import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from "../../config";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import './TrafficMonitor.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const TrafficMonitor = () => {
    const { t } = useTranslation();
    const chartRef = useRef(null);

    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [interfaces, setInterfaces] = useState([]);
    const [selectedInterface, setSelectedInterface] = useState('');
    const [trafficData, setTrafficData] = useState([]);
    const [monitoring, setMonitoring] = useState(false);
    const [intervalId, setIntervalId] = useState(null);

    // -----------------------
    // Fetch Devices
    // -----------------------
    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/device-manage/list`);
                const data = await res.json();
                setDevices(Array.isArray(data) ? data : data.data || data.devices || []);

            } catch (err) {
                console.error('Error fetching devices:', err);
            }
        };
        fetchDevices();
    }, []);

    // -----------------------
    // Device Change -> SNMP Walk
    // -----------------------
    const handleDeviceChange = async (e) => {
        const deviceId = e.target.value;

        setSelectedDevice(deviceId);
        setSelectedInterface('');
        setTrafficData([]);

        if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
        }

        setMonitoring(false);

        const device = devices.find(
            d => String(d.id) === String(deviceId)
        );

        if (!device) return;

        try {
            const res = await fetch(`${API_BASE_URL}/traffic-monitor/snmp_walk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ip: device.ip_address
                }),
            });

            const data = await res.json();
            setInterfaces(data);
        } catch (err) {
            console.error('SNMP Walk Error:', err);
        }
    };

    // -----------------------
    // Start Monitoring (stateless)
    // -----------------------
    const startMonitoring = () => {
        if (!selectedDevice || !selectedInterface) return;

        setMonitoring(true);
        fetchTrafficData();

        const id = setInterval(() => {
            fetchTrafficData();
        }, 5000);

        setIntervalId(id);
    };

    // -----------------------
    // Stop Monitoring
    // -----------------------
    const stopMonitoring = () => {
        if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
        }

        setMonitoring(false);
        setTrafficData([]);
    };

    // -----------------------
    // Fetch Traffic Data (NEW API)
    // -----------------------
    const fetchTrafficData = async () => {
        const device = devices.find(
            d => String(d.id) === String(selectedDevice)
        );

        if (!device || !selectedInterface) return;

        try {
            const res = await fetch(`${API_BASE_URL}/traffic-monitor/traffic_data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ip: device.ip_address,
                    interface_id: selectedInterface
                }),
            });

            const data = await res.json();

            if (data.status !== "success") {
                console.error(data);
                return;
            }

            const timestamp = new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });

            setTrafficData(prev => {
                const updated = [
                    ...prev,
                    {
                        timestamp,
                        in_bps: data.in_bps,
                        out_bps: data.out_bps
                    }
                ];

                return updated.length > 40 ? updated.slice(-40) : updated;
            });

        } catch (err) {
            console.error('Traffic Data Error:', err);
        }
    };

    // -----------------------
    // Cleanup interval
    // -----------------------
    useEffect(() => {
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [intervalId]);

    // -----------------------
    // Format BPS
    // -----------------------
    const formatBps = (bps) => {
        if (!bps) return '0 bps';
        if (bps >= 1_000_000_000) return (bps / 1_000_000_000).toFixed(2) + ' Gbps';
        if (bps >= 1_000_000) return (bps / 1_000_000).toFixed(2) + ' Mbps';
        if (bps >= 1_000) return (bps / 1_000).toFixed(2) + ' Kbps';
        return bps + ' bps';
    };

    const latest = trafficData.at(-1);
    return (
        <div className={`noc-dashboard ${!monitoring ? 'centered-mode' : ''}`}>
            <main className="noc-main">

                {/* Controls */}
                <div className="noc-selection-box">
                    <header className="noc-controls">

                        <div className="noc-group">
                            <select
                                value={selectedDevice}
                                onChange={handleDeviceChange}
                                className="noc-input"
                            >
                                <option value="">-- {t('select_device_option')} --</option>
                                {devices.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.hostname}
                                    </option>
                                ))}
                            </select>

                            {interfaces.length > 0 && (
                                <select
                                    value={selectedInterface}
                                    onChange={(e) => setSelectedInterface(e.target.value)}
                                    className="noc-input"
                                >
                                    <option value="">-- {t('select_interface_option')} --</option>
                                    {interfaces.map(i => (
                                        <option key={i.id} value={i.id}>
                                            {i.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="noc-group">
                            {!monitoring ? (
                                <button
                                    className="noc-btn start"
                                    onClick={startMonitoring}
                                    disabled={!selectedInterface}
                                >
                                    {t('start_monitoring')}
                                </button>
                            ) : (
                                <button
                                    className="noc-btn stop"
                                    onClick={stopMonitoring}
                                >
                                    {t('stop_monitoring')}
                                </button>
                            )}
                        </div>

                    </header>

                    {!monitoring && (
                        <p className="selection-hint">
                            {t('show_live_traffic_status')}
                        </p>
                    )}
                </div>

                {/* Live Stats */}
                {monitoring && (
                    <>
                        <section className="noc-stats">
                            <div className="stat-card rx">
                                <span className="stat-label">{t('incoming')}</span>
                                <span className="stat-value">
                                    {formatBps(latest?.in_bps)}
                                </span>
                            </div>

                            <div className="stat-card tx">
                                <span className="stat-label">{t('outgoing')}</span>
                                <span className="stat-value">
                                    {formatBps(latest?.out_bps)}
                                </span>
                            </div>

                            <div className="stat-card info">
                                <span className="stat-label">{t('interface')}</span>
                                <span className="stat-value">
                                    {
                                        interfaces.find(
                                            i => String(i.id) === String(selectedInterface)
                                        )?.name || 'N/A'
                                    }
                                </span>
                            </div>
                        </section>

                        {/* Chart */}
                        <section className="noc-chart-container">
                            <Line
                                ref={chartRef}
                                data={{
                                    labels: trafficData.map(d => d.timestamp),
                                    datasets: [
                                        {
                                            label: t('incoming'),
                                            data: trafficData.map(d => d.in_bps),
                                            borderColor: '#10b981',
                                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 0,
                                        },
                                        {
                                            label: t('outgoing'),
                                            data: trafficData.map(d => d.out_bps),
                                            borderColor: '#3b82f6',
                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 0,
                                        },
                                    ],
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            display: true,
                                            labels: { color: '#94a3b8' }
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: '#1e293b' },
                                            ticks: { color: '#94a3b8' }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: { color: '#94a3b8' }
                                        }
                                    }
                                }}
                            />
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default TrafficMonitor;